import { Flex } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalBody,
  ModalHeaderDefault,
  Paragraph,
  SearchInput,
  Toggle,
} from 'ui-components';
import { MultiselectBar } from '~/components/MultiselectBar';
import { FEATURE_FLAGS } from '~/constants';
import { CONTENT_SEARCH_AD_HASHTAGS } from '~/hooks/useContentSearch';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { convertPxToRems } from '~/utils-ts';

import { useContentSelection } from '../../blocks/TalentContent/useContentSelection';
import { useAddContentStore } from '../useAddContentStore';
import { AddContent } from './AddContent';
import { AddContentWithMetrics } from './ContentWithMetrics';
import { Platforms } from './Platforms';
import { Posted } from './Posted';
import { SortBy } from './SortBy';
import { FilterType } from './types';

export const ModalAddContent = () => {
  const {
    isModalOpen,
    isEditMode,
    filters,
    searchQuery,
    showInShared,
    setIsModalOpen,
    setFilters,
    removeFilter,
    setSearchQuery,
    setShowInShared,
    reset,
  } = useAddContentStore();

  const {
    selectedContent,
    baseContents,
    modifiedContents,
    handleClose,
    handleAddEditContent,
  } = useContentSelection();

  const { isEnabled: isMediaKitsContentMetrics } = useFeature(
    FEATURE_FLAGS.foamMediaKitsContentMetrics,
  );
  useEffect(() => {
    return () => {
      // Reset the modal when it unmounts
      reset();
    };
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false, undefined);
    reset();
  }, [setIsModalOpen, reset]);

  const handleToggle = useCallback(
    (checked: boolean) => {
      const paidFilter: FilterType = {
        comparator: 'contains',
        name: 'hashtags',
        value: CONTENT_SEARCH_AD_HASHTAGS,
      };

      if (checked) {
        setFilters(paidFilter);
        return;
      }
      removeFilter('hashtags');
    },
    [setFilters, removeFilter],
  );

  const isPaidToggleChecked = useMemo(() => {
    const isChecked = filters.some((f) => f.name === 'hashtags');
    return isChecked;
  }, [filters]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
    },
    [setSearchQuery],
  );

  const handleToggleShowInShared = useCallback(() => {
    setShowInShared(!showInShared);
  }, [showInShared, setShowInShared]);

  return (
    <>
      <Modal isOpen={isModalOpen} onClose={handleModalClose} size="full">
        <ModalHeaderDefault title={`${isEditMode ? 'Edit' : 'Add'} content`} />

        <ModalBody>
          <div className="flex -translate-y-1 flex-col gap-6 bg-neutral-0 pb-16">
            <div className="sticky top-0 z-50 flex flex-col gap-6 bg-neutral-0">
              <Paragraph size="lg" fontWeight="normal">
                Selection is limited to 20. Add more by creating another content
                block.
              </Paragraph>
              <Flex justify="space-between">
                <Flex gap={2} alignItems="center" justifyContent="center">
                  <Platforms />

                  <Posted />
                  <SortBy />

                  {/* Paid Content */}
                  <Toggle
                    id="paid-content-toggle"
                    label="Paid Content"
                    checked={isPaidToggleChecked}
                    onChange={handleToggle}
                  />
                </Flex>

                {/* Search by Keyword */}
                <SearchInput
                  defaultValue={searchQuery}
                  inputProps={{
                    id: 'searchContentId',
                    inputGroupProps: { width: convertPxToRems(194) },
                    name: 'searchContent',
                    onClear: () => handleSearchChange(''),
                    placeholder: 'Search by keywords',
                  }}
                  onChange={handleSearchChange}
                />
              </Flex>
            </div>
            {isMediaKitsContentMetrics ? (
              <AddContentWithMetrics />
            ) : (
              <AddContent />
            )}
          </div>
        </ModalBody>
      </Modal>

      <MultiselectBar
        isOpen={
          [...modifiedContents, ...selectedContent].length > 0 || isEditMode
        }
        numberOfItems={baseContents.length}
        showInShared={showInShared}
        onClose={handleClose}
        onClick={handleAddEditContent}
        onToggle={handleToggleShowInShared}
      />
    </>
  );
};
